/* eslint-disable no-nested-ternary */
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import IconButton from "@mui/material/IconButton";
import BackIcon from "@mui/icons-material/ArrowBackIosNew";

import ActivityTab from "./tabs/activitiy";
import OverviewTab from "./tabs/overview";
import PricesTab from "./tabs/Prices";
import { useNitroStore } from "../../store/Store";
import { useNitroCAStore } from "../../store/CompetitiveAnalysisStore";
import NitroService from "../../utils/nitro.service";
import ILocation from "../../types/ILocation";
import NitroModal from "../../components/Modal";
import ZIndex from "../../styles/zindex";
import { useIsMobile } from "../../hooks/useIsMobile";
import NitroMarker from "../../components/map/components/NitroMarker";
import "./index.css";
import CloseIcon from "../../components/CloseIcon";
import LoadingIcon from "../../components/Loading";
import { removeCity } from "../../utils/crap";
import useEscapeKeyExit from "../../hooks/useEscapeKeyExit";

const InsightsPanel = () => {
  const isTablet = useIsMobile(1334);
  const [
    insightsLocationId,
    showInsightsPanel,
    closeInsightsPanel,
    insightsPanelTab,
    setInsightsPanelTab,
    selectingPinLocation,
    insightsEditing,
    locations,
    loadLocations,
    featureFlags,
  ] = useNitroStore((state) => [
    state.insightsLocationId,
    state.showInsightsPanel,
    state.closeInsightsPanel,
    state.insightsPanelTab,
    state.setInsightsPanelTab,
    state.selectingPinLocation,
    state.insightsEditing,
    state.locations,
    state.loadLocations,
    state.featureFlags
  ]);

  const [displayAddInboundRailPanel] = useNitroCAStore((state) => [
    state.displayAddInboundRailPanel,
  ]);

  const [editing, setEditing] = useState(insightsEditing !== undefined);
  useEffect(() => {
    setEditing(insightsEditing !== undefined);
  }, [insightsEditing]);

  useEscapeKeyExit({
    close: () => {
      closeInsightsPanel();
    },
    active: showInsightsPanel && !editing,
  });

  const [location, setLocation] = useState<ILocation | undefined>(undefined);
  const [pinType, setPinType] = useState<any>(undefined);

  useEffect(() => {
    const match = locations.find((l) => l.id === insightsLocationId);
    if (match) {
      setPinType(match.pinType);
      return;
    }

    // The map pins (state.locations) may not be loaded yet, e.g. when a user
    // opens Location Overview from Favorite Origins before ever loading the
    // map. Fall back to deriving the pin type from the fetched location's
    // occupants so the marker still renders.
    if (location && location.id === insightsLocationId) {
      const occupantTypes = location.occupants.flatMap((o) => o.types);
      const hasKoch = occupantTypes.includes("Koch");
      const hasCompetitor = occupantTypes.includes("Competitor");

      if (hasKoch && hasCompetitor) {
        setPinType("kochAndCompetitor");
      } else if (hasKoch) {
        setPinType("koch");
      } else if (hasCompetitor) {
        setPinType("competitor");
      } else {
        setPinType("customer");
      }
      return;
    }

    setPinType(undefined);
  }, [locations, insightsLocationId, location]);

  const getLocationDetails = useCallback(async () => {
    setLocation(undefined);
    // Add early exit to prevent calls during cleanup
    const shouldLoad = showInsightsPanel || insightsEditing === "Logistics";
    if (!shouldLoad || !insightsLocationId) {
      return;
    }

    try {
      const record = featureFlags.multiRailStation
        ? await NitroService.getLocationDetails2(insightsLocationId)
        : await NitroService.getLocationDetails(insightsLocationId);
      if (showInsightsPanel || insightsEditing === "Logistics") {
        setLocation(record);
      }
    } catch (err) {
      console.error(err);
    }
  }, [
    insightsLocationId,
    showInsightsPanel,
    insightsEditing,
    featureFlags.multiRailStation,
  ]);

  const reload = async () => {
    loadLocations({ panTo: true });
    getLocationDetails();
  };

  useEffect(() => {
    if (!insightsLocationId) return;

    getLocationDetails();
  }, [insightsLocationId, getLocationDetails]);

  // clear location data when the panel is hidden to prevent memory
  const shouldShowPanel = showInsightsPanel || insightsEditing === "Logistics";

  if (!shouldShowPanel) {
    return null;
  }

  // Only treat the fetched location as ready once it matches the currently
  // selected id, so stale data from a prior location never flashes on screen.
  const isLocationReady = !!location && location.id === insightsLocationId;

  const getWidth = (tab: string, mobile: boolean) => {
    if (mobile) {
      return "100%";
    }
    if (tab === "Activity") {
      return 1302;
    }

    return 600;
  };

  let modalClassName = "insightsPanel";
  if (selectingPinLocation) {
    modalClassName = "insightsPanelHidden";
  } else if (isTablet) {
    modalClassName = "";
  }

  const insightsPanelZIndex = displayAddInboundRailPanel
    ? 999
    : isTablet
    ? 10100
    : ZIndex.InsightsPanel;

  const modalStyle =
    isTablet && !displayAddInboundRailPanel
      ? {
          zIndex: insightsPanelZIndex,
          position: "fixed" as const,
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
        }
      : {
          zIndex: insightsPanelZIndex,
        };

  // TODO: backdrop could be replaced with overlay someday
  const panelContent = (
    <>
      {!selectingPinLocation && (
        <div
          id="insightsPanelBackdrop"
          className="insightsPanelBackdrop"
          style={{ zIndex: insightsPanelZIndex - 1 }}
        />
      )}

      {/* Overlay for insights panel */}
      {insightsEditing && !selectingPinLocation && (
        <div
          id="focusOnInsightsPanel"
          style={{
            position: "fixed",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "rgba(15, 44, 82,.5)",
            zIndex: 3,
          }}
        />
      )}

      <NitroModal
        id="InsightsPanel"
        open
        className={modalClassName}
        style={modalStyle}
      >
        <div
          style={{
            zIndex: insightsPanelZIndex,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            width: getWidth(insightsPanelTab, isTablet),
            maxWidth: getWidth(insightsPanelTab, isTablet),
            borderRadius: isTablet ? 0 : 20,
            backgroundColor: "#F7F7F7",
            boxShadow:
              "0px 4px 4px 0px rgba(0, 0, 0, 0.25), 0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
          }}
        >
          {isTablet && (
            <div className="competitiveAnalysisSearchBar">
              <IconButton
                disabled={editing}
                style={{ borderRadius: 2, marginLeft: 6 }}
                onClick={() => {
                  closeInsightsPanel();
                }}
              >
                <span id="logistics-backIcon">
                  <BackIcon />
                </span>
              </IconButton>
            </div>
          )}

          {!isLocationReady || !location ? (
            <LoadingIcon color="#005CA6" />
          ) : (
            <>
              {/* HEADER */}
              <div className="insightsPanelHeader">
                <div
                  className="insightsPanelHeaderFont"
                  style={{
                    fontWeight: 500,
                  }}
                >
                  {removeCity(location.name, location.city)}
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    paddingRight: 16,
                  }}
                >
                  <div>
                    {pinType && <NitroMarker type={pinType} width={35} />}
                  </div>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 0 }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#595959",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {location.address || ""}
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#212122",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {`${location.city}, ${location.state}`}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#595959",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {location.zipCode}
                    </div>
                  </div>
                </div>
                {!isTablet && !insightsEditing && (
                  <div style={{ position: "absolute", right: 8, top: 8 }}>
                    <CloseIcon
                      onClick={() => {
                        closeInsightsPanel();
                      }}
                    />
                  </div>
                )}
              </div>
              {/* END HEADER */}

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  minHeight: 0,
                }}
              >
                {/* TABS  */}
                {!editing && (
                  <div style={{ borderBottom: "2px solid #D9D9D9" }}>
                    <Tabs
                      className="insightsPanelBody"
                      value={insightsPanelTab}
                      onChange={(e, newValue) => setInsightsPanelTab(newValue)}
                    >
                      <Tab
                        value="Overview"
                        label="Overview"
                        disabled={editing}
                      />
                      <Tab value="Activity" label="Activity" disabled />
                    </Tabs>
                  </div>
                )}
                {/* BODY */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    minHeight: 0,
                  }}
                >
                  {insightsPanelTab === "Activity" && <ActivityTab />}
                  {insightsPanelTab === "Overview" && (
                    <OverviewTab location={location} reloadLocation={reload} />
                  )}
                  {insightsPanelTab === "Prices" && <PricesTab />}
                </div>
              </div>
            </>
          )}
        </div>
      </NitroModal>
    </>
  );

  // Only use portal for mobile multiLocQuote to escape overflow:hidden parent
  if (isTablet) {
    return createPortal(panelContent, document.body);
  }

  return panelContent;
};

export default InsightsPanel;
